import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSpreadsheetStore } from './store/spreadsheetStore';
import CellEditor from './CellEditor';
import './SpreadsheetGrid.css';

const CELL_SIZE = 120;
const ROW_HEIGHT = 25;
const COLUMN_WIDTH = 120;
const HEADER_SIZE = 30;

const SpreadsheetGrid = () => {
  const {
    getActiveSheet,
    setActiveCell,
    setSelection,
    updateCell,
    updateFormula,
    updateCellValue,
    recalculateAllFormulas,
    setEditing,
    isEditing,
    getCellValue,
    getCellFormat,
    isMergedCell,
    isMergedParent,
    getMergedRange,
    setColumnWidth,
    setRowHeight,
    getColumnWidth,
    getRowHeight,
    selectRow,
    selectColumn,
    selectAll,
    addressToCell,
    copySelection,
    pasteFromClipboard,
    setCell
  } = useSpreadsheetStore();

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [editorPosition, setEditorPosition] = useState(null);
  const [editorValue, setEditorValue] = useState('');
  const [isResizing, setIsResizing] = useState(false);
  const [resizeType, setResizeType] = useState(null);
  const [resizeIndex, setResizeIndex] = useState(null);
  const [resizeStart, setResizeStart] = useState(null);
  const [editingCell, setEditingCell] = useState({ row: 0, col: 0 });

  const activeSheet = getActiveSheet();
  const activeCell = activeSheet?.activeCell || { row: 0, col: 0 };
  const selection = activeSheet?.selection || { startRow: 0, startCol: 0, endRow: 0, endCol: 0 };

  // Grid boyutları - sayfadaki maksimum satır/sütun sayısını al
  const getGridDimensions = () => {
    const sheet = getActiveSheet();
    if (!sheet || !sheet.grid) return { rows: 50, cols: 50 };
    
    let maxRow = 0;
    let maxCol = 0;
    
    // Gerçekte veri olan en büyük satır/sütunu bul
    for (let row = 0; row < sheet.grid.length; row++) {
      for (let col = 0; col < sheet.grid[row].length; col++) {
        const cell = sheet.grid[row][col];
        if (cell && (cell.value || cell.formula)) {
          maxRow = Math.max(maxRow, row);
          maxCol = Math.max(maxCol, col);
        }
      }
    }
    
    // En az 50x50 göster, maksimum 200x200
    return {
      rows: Math.min(Math.max(maxRow + 10, 50), 200),
      cols: Math.min(Math.max(maxCol + 10, 50), 200)
    };
  };
  
  const gridDims = getGridDimensions();
  const totalRows = gridDims.rows;
  const totalCols = gridDims.cols;

  // Sütun başlıkları (A, B, C, ..., Z, AA, AB, ...)
  const columnHeaders = Array.from({ length: totalCols }, (_, i) => {
    let result = '';
    let num = i;
    while (num >= 0) {
      result = String.fromCharCode(65 + (num % 26)) + result;
      num = Math.floor(num / 26) - 1;
    }
    return result;
  });

  // Satır başlıkları (1, 2, 3, ...)
  const rowHeaders = Array.from({ length: totalRows }, (_, i) => i + 1);

  // Seçim kontrolü
  const isInSelection = (row, col) => {
    // Selection güvenlik kontrolü
    if (!selection || typeof selection.startRow !== 'number' || typeof selection.startCol !== 'number' ||
        typeof selection.endRow !== 'number' || typeof selection.endCol !== 'number') {
      return false;
    }

    const { startRow, startCol, endRow, endCol } = selection;
    
    // Tek hücre seçimi kontrolü
    if (startRow === endRow && startCol === endCol) {
      return row === startRow && col === startCol;
    }
    
    // Çoklu hücre seçimi kontrolü
    const minRow = Math.min(startRow, endRow);
    const maxRow = Math.max(startRow, endRow);
    const minCol = Math.min(startCol, endCol);
    const maxCol = Math.max(startCol, endCol);
    
    return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol;
  };

  // Hücre tıklama
  const handleCellClick = (row, col) => {
    // Birleştirilmiş hücre kontrolü
    const isMerged = isMergedCell(row, col);
    const isParent = isMergedParent(row, col);
    const mergedRange = getMergedRange(row, col);
    
    // Eğer birleştirilmiş hücrenin bir parçasıysa, ana hücreyi seç
    if (isMerged && !isParent && mergedRange) {
      setActiveCell(mergedRange.startRow, mergedRange.startCol);
      setSelection(mergedRange.startRow, mergedRange.startCol, mergedRange.endRow, mergedRange.endCol);
    } else {
      setActiveCell(row, col);
      setSelection(row, col, row, col);
    }
  };

  // Excel'deki gibi hücre referansı seçimi
  const handleCellReferenceClick = (row, col) => {
    const cellAddress = `${String.fromCharCode(65 + col)}${row + 1}`;
    
    // Eğer editör açıksa, hücre referansını ekle
    if (isEditing) {
      const currentValue = editorValue;
      const newValue = currentValue + cellAddress;
      setEditorValue(newValue);
    }
  };

  // Ctrl+Click ile çoklu seçim
  const handleCellCtrlClick = (row, col) => {
    const currentActiveCell = activeCell || { row: 0, col: 0 };
    const startRow = Math.min(currentActiveCell.row, row);
    const endRow = Math.max(currentActiveCell.row, row);
    const startCol = Math.min(currentActiveCell.col, col);
    const endCol = Math.max(currentActiveCell.col, col);
    
    setSelection(startRow, startCol, endRow, endCol);
  };

  // Hücre tıklama (Shift ile genişletme)
  const handleCellClickWithShift = (row, col) => {
    const currentActiveCell = activeCell || { row: 0, col: 0 };
    const startRow = Math.min(currentActiveCell.row, row);
    const endRow = Math.max(currentActiveCell.row, row);
    const startCol = Math.min(currentActiveCell.col, col);
    const endCol = Math.max(currentActiveCell.col, col);
    
    setSelection(startRow, startCol, endRow, endCol);
  };

  // Sütun boyutlandırma başlatma
  const handleColumnResizeStart = (e, colIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeType('column');
    setResizeIndex(colIndex);
    setResizeStart(e.clientX);
  };

  // Satır boyutlandırma başlatma
  const handleRowResizeStart = (e, rowIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeType('row');
    setResizeIndex(rowIndex);
    setResizeStart(e.clientY);
  };

  // Hücre çift tıklama
  const handleCellDoubleClick = (row, col) => {
    // Birleştirilmiş hücre kontrolü
    const isMerged = isMergedCell(row, col);
    const isParent = isMergedParent(row, col);
    const mergedRange = getMergedRange(row, col);
    
    let targetRow = row;
    let targetCol = col;
    
    // Eğer birleştirilmiş hücrenin bir parçasıysa, ana hücreyi hedefle
    if (isMerged && !isParent && mergedRange) {
      targetRow = mergedRange.startRow;
      targetCol = mergedRange.startCol;
    }
    
    setActiveCell(targetRow, targetCol);
    setSelection(targetRow, targetCol, targetRow, targetCol);
    setEditing(true);
    setEditingCell({ row: targetRow, col: targetCol });
    
    // Editör pozisyonunu hesapla - hedef hücrenin grid içindeki pozisyonunu kullan
    let currentLeft = HEADER_SIZE;
    for (let i = 0; i < targetCol; i++) {
      currentLeft += getColumnWidth(i);
    }
    
    let currentTop = HEADER_SIZE;
    for (let i = 0; i < targetRow; i++) {
      currentTop += getRowHeight(i);
    }
    
    setEditorPosition({
      top: currentTop,
      left: currentLeft,
      width: getColumnWidth(targetCol),
      height: getRowHeight(targetRow)
    });
    
    // Hücrenin formülünü veya değerini göster
    const cell = activeSheet.grid[targetRow]?.[targetCol];
    const displayValue = cell?.formula || cell?.value || '';
    setEditorValue(displayValue);
  };

  // Mouse down
  const handleCellMouseDown = (e, row, col) => {
    if (e.button === 0) { // Sol tık
      // Birleştirilmiş hücre kontrolü
      const isMerged = isMergedCell(row, col);
      const isParent = isMergedParent(row, col);
      const mergedRange = getMergedRange(row, col);
      
      let targetRow = row;
      let targetCol = col;
      
      // Eğer birleştirilmiş hücrenin bir parçasıysa, ana hücreyi hedefle
      if (isMerged && !isParent && mergedRange) {
        targetRow = mergedRange.startRow;
        targetCol = mergedRange.startCol;
      }
      
      setIsSelecting(true);
      setSelectionStart({ row: targetRow, col: targetCol });
      setActiveCell(targetRow, targetCol);
      setSelection(targetRow, targetCol, targetRow, targetCol);
    }
  };

  // Mouse enter
  const handleCellMouseEnter = (row, col) => {
    if (isSelecting && selectionStart) {
      const startRow = Math.min(selectionStart.row, row);
      const endRow = Math.max(selectionStart.row, row);
      const startCol = Math.min(selectionStart.col, col);
      const endCol = Math.max(selectionStart.col, col);
      
      setSelection(startRow, startCol, endRow, endCol);
    }
  };

  // Mouse up ve move
  useEffect(() => {
    const handleMouseUp = () => {
      setIsSelecting(false);
      setSelectionStart(null);
      setIsResizing(false);
      setResizeType(null);
      setResizeIndex(null);
      setResizeStart(null);
      document.body.classList.remove('resizing', 'resizing-row');
    };

    const handleMouseMove = (e) => {
      if (isResizing && resizeType && resizeIndex !== null && resizeStart !== null) {
        e.preventDefault();
        
        if (resizeType === 'column') {
          document.body.classList.add('resizing');
          const deltaX = e.clientX - resizeStart;
          const currentWidth = getColumnWidth(resizeIndex);
          const newWidth = Math.max(50, currentWidth + deltaX);
          setColumnWidth(resizeIndex, newWidth);
          setResizeStart(e.clientX);
        } else if (resizeType === 'row') {
          document.body.classList.add('resizing-row');
          const deltaY = e.clientY - resizeStart;
          const currentHeight = getRowHeight(resizeIndex);
          const newHeight = Math.max(20, currentHeight + deltaY);
          setRowHeight(resizeIndex, newHeight);
          setResizeStart(e.clientY);
        }
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isResizing, resizeType, resizeIndex, resizeStart, getColumnWidth, getRowHeight, setColumnWidth, setRowHeight]);

  // Klavye kısayolları - F2, Ctrl+C, Ctrl+V
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Dialog açıkken global keyboard handler'ı devre dışı bırak
      if (document.querySelector('[role="dialog"]')) {
        return;
      }
      
      // Eğer hücre düzenleme modundaysa, hiçbir event'i handle etme
      if (isEditing) {
        return;
      }
      
      // Input alanları focus'tayken keyboard handler'ı devre dışı bırak
      const activeElement = document.activeElement;
      if (activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.contentEditable === 'true'
      )) {
        return;
      }
      
      // Ctrl+C - Kopyala
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        copySelection();
        console.log('📋 Ctrl+C ile kopyalandı');
        return;
      }
      
      // Ctrl+V - Yapıştır
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        const currentActiveCell = activeCell || { row: 0, col: 0 };
        pasteFromClipboard(currentActiveCell.row, currentActiveCell.col);
        console.log('📋 Ctrl+V ile yapıştırıldı');
        return;
      }
      
      // Delete tuşu - Seçili hücreleri temizle
      if (e.key === 'Delete') {
        e.preventDefault();
        if (activeCell) {
          setCell(activeCell.row, activeCell.col, { value: '', formula: '', displayValue: '' });
          console.log('🗑️ Hücre temizlendi');
        }
        return;
      }
      
      // F2 tuşu - Hücre düzenleme
      if (e.key === 'F2') {
        e.preventDefault();
        const currentActiveCell = activeCell || { row: 0, col: 0 };
        setEditing(true);
        setEditingCell({ row: currentActiveCell.row, col: currentActiveCell.col });
        
        let currentLeft = HEADER_SIZE;
        for (let i = 0; i < currentActiveCell.col; i++) {
          currentLeft += getColumnWidth(i);
        }
        
        let currentTop = HEADER_SIZE;
        for (let i = 0; i < currentActiveCell.row; i++) {
          currentTop += getRowHeight(i);
        }
        
        setEditorPosition({
          top: currentTop,
          left: currentLeft,
          width: getColumnWidth(currentActiveCell.col),
          height: getRowHeight(currentActiveCell.row)
        });
        
        const cell = getActiveSheet()?.grid?.[currentActiveCell.row]?.[currentActiveCell.col];
        setEditorValue(cell?.formula || cell?.value || '');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, getActiveSheet, setEditing, getColumnWidth, getRowHeight, activeCell, copySelection, pasteFromClipboard, setCell]);

  // YENİ: Çoklu hücre seçimi için mouse event'leri
  const handleMouseDown = (e, row, col) => {
    if (e.button !== 0) return; // Sadece sol tık
    
    if (e.ctrlKey || e.metaKey) {
      // Ctrl+Click: Çoklu seçim
      const { selection } = getActiveSheet();
      const newEndRow = row;
      const newEndCol = col;
      
      // Mevcut seçimi genişlet
      const minRow = Math.min(selection.startRow, selection.endRow, newEndRow);
      const maxRow = Math.max(selection.startRow, selection.endRow, newEndRow);
      const minCol = Math.min(selection.startCol, selection.endCol, newEndCol);
      const maxCol = Math.max(selection.startCol, selection.endCol, newEndCol);
      
      setSelection(minRow, minCol, maxRow, maxCol);
    } else if (e.shiftKey) {
      // Shift+Click: Aralık seçimi
      const { activeCell } = getActiveSheet();
      setSelection(activeCell.row, activeCell.col, row, col);
    } else {
      // Normal tık: Tek hücre seçimi
      setActiveCell(row, col);
      setSelection(row, col, row, col);
    }
    
    setIsSelecting(true);
    setSelectionStart({ row, col });
  };

  const handleMouseEnter = (e, row, col) => {
    if (isSelecting && selectionStart) {
      // Sürükleme ile seçim genişletme
      const { activeCell } = getActiveSheet();
      setSelection(activeCell.row, activeCell.col, row, col);
    }
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
    setSelectionStart(null);
  };

  // Editör değişiklik
  const handleEditorChange = (value) => {
    setEditorValue(value);
  };

  // Editör kaydetme
  const handleEditorSave = (value) => {
    // Formül mü yoksa değer mi kontrol et
    if (value && value.startsWith('=')) {
      // Formül ise updateFormula kullan (otomatik hesaplama)
      updateFormula(editingCell.row, editingCell.col, value);
      
      // Excel benzeri bildirim
      console.log(`✅ Formül hesaplandı: ${value}`);
    } else {
      // Değer ise updateCellValue kullan (otomatik hesaplama)
      updateCellValue(editingCell.row, editingCell.col, value);
      
      // Excel benzeri bildirim
      console.log(`✅ Değer güncellendi: ${value}`);
    }
    
    setEditing(false);
    setEditorPosition(null);
    setEditingCell({ row: 0, col: 0 });
  };

  // Editör iptal
  const handleEditorCancel = () => {
    setEditing(false);
    setEditorPosition(null);
    setEditingCell({ row: 0, col: 0 });
  };

  // Grid render fonksiyonu
  const renderGrid = () => {
    // ActiveSheet kontrolü
    if (!activeSheet || !activeSheet.grid) {
      return <div>Loading...</div>;
    }

    // Selection güvenli kontrolü
    const safeSelection = selection || { startRow: 0, startCol: 0, endRow: 0, endCol: 0 };
    const safeActiveCell = activeCell || { row: 0, col: 0 };

    const grid = [];

    // Toplam genişlik ve yükseklik hesaplama
    let totalWidth = HEADER_SIZE;
    let totalHeight = HEADER_SIZE;
    
    for (let col = 0; col < totalCols; col++) {
      totalWidth += getColumnWidth(col);
    }
    
    for (let row = 0; row < totalRows; row++) {
      totalHeight += getRowHeight(row);
    }

    // Köşe hücresi
    grid.push(
      <div key="corner" className="grid-corner" style={{ width: HEADER_SIZE, height: HEADER_SIZE }}>
        <div 
          className="corner-cell"
          onClick={selectAll}
          title="Tümünü Seç"
        ></div>
      </div>
    );

    // Sütun başlıkları
    let currentLeft = HEADER_SIZE;
    for (let col = 0; col < totalCols; col++) {
      const colIndex = col;
      const currentWidth = getColumnWidth(colIndex);
      const isSelected = safeSelection.startCol <= colIndex && safeSelection.endCol >= colIndex;
      
      grid.push(
        <div 
          key={`col-${col}`} 
          className={`column-header ${isSelected ? 'selected' : ''}`}
          style={{ 
            width: currentWidth, 
            height: HEADER_SIZE,
            position: 'absolute',
            left: currentLeft,
            top: 0
          }}
        >
          <div 
            className="header-cell"
            onClick={() => selectColumn(colIndex)}
            onDoubleClick={() => selectColumn(colIndex)}
          >
            {columnHeaders[col]}
          </div>
          <div 
            className="resize-handle column-resize"
            onMouseDown={(e) => handleColumnResizeStart(e, colIndex)}
            style={{ right: 0 }}
          />
        </div>
      );
      
      currentLeft += currentWidth;
    }

    // Satır başlıkları
    let currentTop = HEADER_SIZE;
    for (let row = 0; row < totalRows; row++) {
      const rowIndex = row;
      const currentHeight = getRowHeight(rowIndex);
      const isSelected = safeSelection.startRow <= rowIndex && safeSelection.endRow >= rowIndex;
      
      grid.push(
        <div 
          key={`row-${row}`} 
          className={`row-header ${isSelected ? 'selected' : ''}`}
          style={{ 
            width: HEADER_SIZE, 
            height: currentHeight,
            position: 'absolute',
            left: 0,
            top: currentTop
          }}
        >
          <div 
            className="header-cell"
            onClick={() => selectRow(rowIndex)}
            onDoubleClick={() => selectRow(rowIndex)}
          >
            {rowHeaders[row]}
          </div>
          <div 
            className="resize-handle row-resize"
            onMouseDown={(e) => handleRowResizeStart(e, rowIndex)}
            style={{ bottom: 0 }}
          />
        </div>
      );
      
      currentTop += currentHeight;
    }

    // Normal hücreler
    let cellTop = HEADER_SIZE;
    for (let row = 0; row < totalRows; row++) {
      let cellLeft = HEADER_SIZE;
      for (let col = 0; col < totalCols; col++) {
        const cellRow = row;
        const cellCol = col;
        
        // Birleştirilmiş hücre kontrolü
        const isMerged = isMergedCell(cellRow, cellCol);
        const isParent = isMergedParent(cellRow, cellCol);
        const mergedRange = getMergedRange(cellRow, cellCol);
        
        // Eğer bu hücre birleştirilmiş ve ana hücre değilse, gizle
        if (isMerged && !isParent) {
          cellLeft += getColumnWidth(cellCol);
          continue;
        }
        
        // Birleştirilmiş hücre için değer ve format al
        let actualRow = cellRow;
        let actualCol = cellCol;
        
        if (isMerged && !isParent) {
          actualRow = mergedRange.startRow;
          actualCol = mergedRange.startCol;
        }
        
        const cellValue = getCellValue(actualRow, actualCol);
        const cellFormat = getCellFormat(actualRow, actualCol);
        const cell = getActiveSheet().grid[actualRow]?.[actualCol];
        const hasFormula = cell?.formula && cell.formula.startsWith('=');
        const isActive = safeActiveCell.row === cellRow && safeActiveCell.col === cellCol;
        const isSelected = isInSelection(cellRow, cellCol);

        // Border stilini oluştur
        const getBorderStyle = (border) => {
          if (!border) return '1px solid #e0e0e0';
          
          const { top, right, bottom, left } = border;
          const borderParts = [];
          
          // Her bir kenar için border stilini oluştur
          if (top && top.style !== 'none') {
            borderParts.push(`border-top: 1px ${top.style} ${top.color || '#000000'}`);
          }
          if (right && right.style !== 'none') {
            borderParts.push(`border-right: 1px ${right.style} ${right.color || '#000000'}`);
          }
          if (bottom && bottom.style !== 'none') {
            borderParts.push(`border-bottom: 1px ${bottom.style} ${bottom.color || '#000000'}`);
          }
          if (left && left.style !== 'none') {
            borderParts.push(`border-left: 1px ${left.style} ${left.color || '#000000'}`);
          }
          
          return borderParts.length > 0 ? borderParts.join('; ') : '1px solid #e0e0e0';
        };

        // Birleştirilmiş hücre için stil hesaplama
        let cellStyle = {
          width: getColumnWidth(cellCol),
          height: getRowHeight(cellRow),
          position: 'absolute',
          left: cellLeft,
          top: cellTop,
          backgroundColor: cellFormat.backgroundColor || '#ffffff',
          color: cellFormat.textColor || '#000000',
          fontFamily: cellFormat.fontFamily || 'Arial',
          fontSize: cellFormat.fontSize || 11,
          fontWeight: cellFormat.bold ? 'bold' : 'normal',
          fontStyle: cellFormat.italic ? 'italic' : 'normal',
          textDecoration: cellFormat.underline ? 'underline' : 'none',
          textAlign: cellFormat.horizontalAlign || 'left',
          verticalAlign: cellFormat.verticalAlign || 'top',
          borderTop: cellFormat.border?.top?.style !== 'none' ? `1px ${cellFormat.border?.top?.style || 'solid'} ${cellFormat.border?.top?.color || '#000'}` : '1px solid #e0e0e0',
          borderRight: cellFormat.border?.right?.style !== 'none' ? `1px ${cellFormat.border?.right?.style || 'solid'} ${cellFormat.border?.right?.color || '#000'}` : '1px solid #e0e0e0',
          borderBottom: cellFormat.border?.bottom?.style !== 'none' ? `1px ${cellFormat.border?.bottom?.style || 'solid'} ${cellFormat.border?.bottom?.color || '#000'}` : '1px solid #e0e0e0',
          borderLeft: cellFormat.border?.left?.style !== 'none' ? `1px ${cellFormat.border?.left?.style || 'solid'} ${cellFormat.border?.left?.color || '#000'}` : '1px solid #e0e0e0',
          padding: '2px 4px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'normal',
          display: 'flex',
          alignItems: 'center',
          justifyContent: cellFormat.horizontalAlign === 'center' ? 'center' : 
                         cellFormat.horizontalAlign === 'right' ? 'flex-end' : 'flex-start'
        };

        // Formül hücreleri için özel stil
        if (hasFormula) {
          cellStyle.fontStyle = 'italic';
          cellStyle.color = '#1976d2';
        }

        // Hata durumu için stil
        if (cellValue === '#ERROR!') {
          cellStyle.backgroundColor = '#ffebee';
          cellStyle.color = '#c62828';
          cellStyle.border = '1px solid #ef5350';
        }

        // Birleştirilmiş hücre için genişlik ve yükseklik ayarla
        if (isParent && mergedRange) {
          const { startRow, startCol, endRow, endCol } = mergedRange;
          
          let totalWidth = 0;
          let totalHeight = 0;
          let mergedLeft = HEADER_SIZE;
          let mergedTop = HEADER_SIZE;
          
          for (let i = 0; i < startCol; i++) {
            mergedLeft += getColumnWidth(i);
          }
          
          for (let i = 0; i < startRow; i++) {
            mergedTop += getRowHeight(i);
          }
          
          for (let i = startCol; i <= endCol; i++) {
            totalWidth += getColumnWidth(i);
          }
          
          for (let i = startRow; i <= endRow; i++) {
            totalHeight += getRowHeight(i);
          }
          
          cellStyle = {
            ...cellStyle,
            width: totalWidth,
            height: totalHeight,
            left: mergedLeft,
            top: mergedTop,
            zIndex: 5,
            border: '2px solid #1976d2'
          };
        }

        grid.push(
          <div
            key={`cell-${row}-${col}`}
            style={cellStyle}
            className={`grid-cell ${isActive ? 'active' : ''} ${isSelected ? 'selected' : ''} ${isMerged ? 'merged' : ''} ${isParent ? 'merged-parent' : ''} ${hasFormula ? 'has-formula' : ''} ${cellValue === '#ERROR!' ? 'error' : ''}`}
            data-row={cellRow}
            data-col={cellCol}
            data-has-formula={hasFormula}
            data-formula={hasFormula ? cell.formula : ''}
            data-value={cellValue}
            onClick={(e) => handleCellClick(cellRow, cellCol)}
            onDoubleClick={() => handleCellDoubleClick(cellRow, cellCol)}
            onMouseDown={(e) => handleMouseDown(e, cellRow, cellCol)}
            onMouseEnter={(e) => handleMouseEnter(e, cellRow, cellCol)}
          >
            {cellValue}
          </div>
        );
        
        cellLeft += getColumnWidth(cellCol);
      }
      cellTop += getRowHeight(row);
    }

    return grid;
  };

  // Grid container boyutlarını hesapla
  const getGridContainerDimensions = () => {
    let totalWidth = HEADER_SIZE;
    let totalHeight = HEADER_SIZE;
    
    for (let col = 0; col < totalCols; col++) {
      totalWidth += getColumnWidth(col);
    }
    
    for (let row = 0; row < totalRows; row++) {
      totalHeight += getRowHeight(row);
    }
    
    return { width: totalWidth, height: totalHeight };
  };

  const gridContainerDimensions = getGridContainerDimensions();

  return (
    <div className="spreadsheet-grid">
      <div 
        className="grid-container"
        style={{
          width: gridContainerDimensions.width,
          height: gridContainerDimensions.height,
          position: 'relative'
        }}
      >
        {renderGrid()}
      </div>

      {/* Hücre editörü */}
      {isEditing && editorPosition && (
        <CellEditor
          value={editorValue}
          position={editorPosition}
          onChange={handleEditorChange}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
        />
      )}
    </div>
  );
};

export default React.memo(SpreadsheetGrid);